import { formatScore } from '../../utils/scoreFormat';
import { getComparisonSummary } from '../../utils/resultInsights';
import { getInterpretationTone } from '../../utils/interpretationTone';
import '../Results/Results.css';

export function getAnswerContribution(question, answerIndex) {
  if (!question?.scores || !Array.isArray(question.scores)) {
    return null;
  }
  if (typeof answerIndex !== 'number' || answerIndex < 0 || answerIndex >= question.scores.length) {
    return null;
  }
  return Number(question.scores[answerIndex]);
}

export function scoreInInterpretationRange(score, row) {
  const s = Number(score);
  if (Number.isNaN(s)) return false;
  const min = Number(row?.minScore);
  const max = Number(row?.maxScore);
  if (Number.isNaN(min) || Number.isNaN(max)) return false;
  return s >= min && s <= max;
}

/** Полный разбор одного сохранённого результата (для психолога). */
export default function TestResultBreakdown({ result }) {
  const questions = result.testSnapshot?.questions || [];
  const interpretations = result.testSnapshot?.scoreInterpretations || [];
  const comparisonSummary = getComparisonSummary(result.comparison);
  const interp = result.interpretation;
  const interpretationTone = getInterpretationTone(interp?.color);

  return (
    <div className="p-2">
      {interp && (
        <div className={`border rounded p-3 mb-3 interpretation-breakdown tone-${interpretationTone}`}>
          <h6 className="mb-2">Интерпретация (как в кабинете пользователя)</h6>
          <div className="small text-muted mb-1">{interp.level}</div>
          {interp.interpretation && <p className="mb-2">{interp.interpretation}</p>}
          {interp.recommendation && (
            <div className="small">
              <strong>Рекомендация:</strong> {interp.recommendation}
            </div>
          )}
        </div>
      )}

      {comparisonSummary && (
        <div className={`insight-card comparison-card ${comparisonSummary.tone} mb-3`}>
          <h6 className="mb-2">{comparisonSummary.title}</h6>
          <p className="small mb-1">{comparisonSummary.description}</p>
          {result.comparison?.previousDate && (
            <p className="text-muted small mb-0">
              Предыдущее прохождение: {new Date(result.comparison.previousDate).toLocaleString('ru-RU')}
            </p>
          )}
        </div>
      )}

      {interpretations.length > 0 && (
        <div className="mb-3">
          <h6 className="mb-2">Шкала интерпретации методики</h6>
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Диапазон баллов</th>
                  <th>Уровень</th>
                  <th>Интерпретация</th>
                </tr>
              </thead>
              <tbody>
                {interpretations.map((row, idx) => {
                  const activeRange = scoreInInterpretationRange(result.score, row);
                  return (
                    <tr
                      key={`interp-${result._id}-${idx}`}
                      className={activeRange ? `fw-semibold interpretation-table-row-active tone-${interpretationTone}` : undefined}
                    >
                      <td>
                        {formatScore(row.minScore)} — {formatScore(row.maxScore)}
                        {activeRange ? (
                          <span className={`ms-1 small interpretation-accent tone-${interpretationTone}`}>
                            (балл пользователя)
                          </span>
                        ) : null}
                      </td>
                      <td>{row.level || '—'}</td>
                      <td className="small">{row.interpretation || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h6 className="mb-2">Вопросы и варианты ответов</h6>
      {questions.length === 0 ? (
        <div className="text-muted">В этом результате нет снимка вопросов.</div>
      ) : (
        <ol className="mb-0">
          {questions.map((question, index) => {
            const answerIndex = result.answers?.[index];
            const contribution = getAnswerContribution(question, answerIndex);
            const hasScores = Array.isArray(question.scores) && question.scores.length > 0;
            const options = Array.isArray(question.options) ? question.options : [];

            return (
              <li key={`${result._id}-q-${index}`} className="mb-3">
                <div>
                  <strong>{question.text || `Вопрос ${index + 1}`}</strong>
                </div>
                {hasScores && contribution !== null && (
                  <div className="text-muted small mb-1">
                    Выбранный ответ даёт <strong>{formatScore(contribution)}</strong> к суммарному баллу
                  </div>
                )}
                {!hasScores && (
                  <div className="text-muted small mb-1">Баллы за варианты в снимке недоступны.</div>
                )}
                <ul className="small mb-0 list-unstyled">
                  {options.map((optText, optIdx) => {
                    const scoreVal =
                      hasScores && optIdx < question.scores.length ? Number(question.scores[optIdx]) : null;
                    const selected = answerIndex === optIdx;
                    return (
                      <li
                        key={`${result._id}-q-${index}-o-${optIdx}`}
                        className={
                          selected
                            ? `fw-semibold mb-1 answer-selected tone-${interpretationTone}`
                            : 'mb-1'
                        }
                      >
                        {optText}
                        {scoreVal !== null && !Number.isNaN(scoreVal) ? ` — ${formatScore(scoreVal)}` : ''}
                        {selected ? (
                          <span className={`ms-1 interpretation-accent tone-${interpretationTone}`}>
                            (ответ пользователя)
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
